/**
 * The app shell — sidebar, organization switcher, navigation, user block.
 *
 * Ported from design/preview/message-log.html. The structure and class names
 * are the design's; what is added here is the behaviour behind them.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { api } from "../api/client";
import { useAsync, useDismiss } from "../hooks";
import { Icon, type IconName } from "../components/Sprites";
import { AttentionStrip, attentionFor, severityFor } from "./AttentionStrip";
import { CommandPalette } from "./CommandPalette";
import { SendingBanner } from "./SendingBanner";
import { roleLabel } from "../roles";
import { useSession } from "../session";
import type { AttentionItem, AttentionReport, Capability } from "../api/types";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;

  /**
   * The capability this screen needs to be worth showing.
   *
   * Absent means everybody sees it. A screen whose whole content would be a
   * 403 is hidden rather than drawn — but hidden from the *nav* only: typing
   * the address still reaches the screen, which then explains itself. Nav is a
   * shortlist, not a security boundary, and the server checks every request
   * regardless.
   */
  needs?: Capability;

  /**
   * Shown only to a deployment operator.
   *
   * A separate gate rather than another capability, because it is not one:
   * capabilities are held *within* an organization and resolved from a
   * membership, and an operator is not a member of every tenant. Expressing it
   * as `needs` would mean inventing a capability nobody can be granted, which
   * would then have to be excluded from every preset by hand.
   */
  operatorOnly?: boolean;
}

interface NavSection {
  /** null for the first group, which is the one screen that needs no heading. */
  heading: string | null;
  items: NavItem[];
}

/**
 * Only screens that exist, in four groups plus the log.
 *
 * Thirteen items in one flat column asks the reader to hold the whole product
 * in their head to find anything. The order was already the order the work
 * happens — who you are sending to, what you are sending, the send itself —
 * and the headings just say that out loud.
 *
 * MESSAGE LOG SITS ALONE AND ABOVE. It is the screen this product is built
 * around and the one people return to; putting it under a heading would make
 * it a member of a category, which is exactly what it is not.
 *
 * The design (design/preview/message-log.html:83) draws the flat list. This
 * keeps its order and its classes and adds the headings the design's own
 * `.oc-sidebar__section` was already built for.
 */
const NAV: NavSection[] = [
  {
    heading: null,
    items: [{ to: "/messages", label: "Message Log", icon: "log" }],
  },
  {
    heading: "Audience",
    items: [
      { to: "/contacts", label: "Contacts", icon: "contacts" },
      { to: "/groups", label: "Groups", icon: "groups" },
      { to: "/forms", label: "Forms", icon: "form", needs: "forms:manage" },
    ],
  },
  {
    heading: "Content",
    items: [
      { to: "/templates", label: "Templates", icon: "template" },
      { to: "/campaigns", label: "Campaigns", icon: "campaign", needs: "campaigns:write" },
    ],
  },
  {
    heading: "Delivery",
    items: [
      { to: "/analytics", label: "Analytics", icon: "chart" },
      { to: "/domains", label: "Domains", icon: "dns", needs: "domains:manage" },
      { to: "/suppressions", label: "Suppressions", icon: "warning" },
    ],
  },
  {
    heading: "Platform",
    items: [
      { to: "/keys", label: "API Keys", icon: "key", needs: "keys:manage" },
      // Labelled Webhooks, routed at /endpoints. `/webhooks` on this origin is
      // the inbound provider-callback prefix — SES, Mailgun and Postmark hold
      // that URL — so the API router answers it before the SPA fallback ever
      // runs, and the screen served a 404 problem document instead. That prefix
      // is in provider consoles and cannot move; this one can.
      { to: "/endpoints", label: "Webhooks", icon: "webhook", needs: "webhooks:manage" },
      { to: "/teams", label: "Teams", icon: "team", needs: "teams:manage" },
      { to: "/settings", label: "Settings", icon: "settings" },
    ],
  },
  {
    // Its own section rather than an item under Platform, because it is not
    // about this organization at all — it spans every one of them. Nobody but
    // an operator sees the heading, and a heading over nothing is not
    // rendered, so for everyone else the sidebar is unchanged.
    heading: "Deployment",
    items: [{ to: "/tenants", label: "Tenants", icon: "building", operatorOnly: true }],
  },
];

/** Every route, in nav order. */
const ROUTES: NavItem[] = NAV.flatMap((section) => section.items);

/**
 * Whether a nav item is shown — the one rule, for the sidebar and the palette
 * alike.
 *
 * THERE USED TO BE TWO COPIES, AND THEY DRIFTED. The sidebar filtered on
 * `needs` and `operatorOnly`; the palette filtered its own list on `needs`
 * alone, because `operatorOnly` was added to the sidebar's rule when the
 * operator view shipped and nobody reopened the other. `/tenants` has no
 * `needs`, so ⌘K offered Tenants to every user in every organization — a dead
 * end rather than a leak, since the server refuses a non-operator, but a screen
 * the sidebar correctly hid.
 *
 * The compiler could not have caught it. The palette's prop type named only the
 * fields it filtered on, and TypeScript accepts a wider object where a narrower
 * one is expected, so the field that changed the rule was dropped silently at
 * the boundary. The palette is now handed routes this rule has already
 * filtered, and has no rule of its own to fall behind.
 */
function useNavRule(): (item: NavItem) => boolean {
  const { session, can } = useSession();

  // Not a capability, so it cannot go through can(). See NavItem.operatorOnly.
  const isOperator = session?.user.is_platform_operator === true;

  return (item) => (item.operatorOnly ? isOperator : !item.needs || can(item.needs));
}

export function Shell({ children }: { children: ReactNode }) {
  const { orgId } = useSession();
  const shown = useNavRule();

  // Fetched once here rather than by the strip and the sidebar separately —
  // they render the same report two ways, and two hooks would be two requests
  // that can disagree with each other for a moment.
  const { data } = useAsync<AttentionReport>(
    (signal) => api.get<AttentionReport>("/v1/attention", { signal }),
    [orgId],
  );

  const items = data?.items ?? [];

  // The palette's open state lives here rather than in the sidebar, because the
  // shortcut is global and the overlay covers the whole window. Owning it in
  // the sidebar would mean the sidebar rendering a fixed-position dialog over
  // the main column, which is the sort of thing that works until somebody adds
  // a stacking context.
  const [palette, setPalette] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette((v) => !v);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="app">
      <Sidebar items={items} onOpenPalette={() => setPalette(true)} />
      <div className="main">
        <SendingBanner />
        <AttentionStrip items={items} />
        {children}
      </div>
      <CommandPalette
        open={palette}
        onClose={() => setPalette(false)}
        // The rule the sidebar draws with, applied before the palette sees
        // anything — it keeps no filter of its own to drift.
        routes={ROUTES.filter(shown)}
      />
    </div>
  );
}

function Sidebar({ items, onOpenPalette }: { items: AttentionItem[]; onOpenPalette: () => void }) {
  const { session, orgId, role, logout } = useSession();
  const active = session?.organizations.find((o) => o.id === orgId);
  const shown = useNavRule();
  const [collapsed, toggleCollapsed] = useCollapsed(session?.user.id);

  // Collapsed, a label leaves the screen and stays the name of its control.
  const label = collapsed ? "oc-visually-hidden" : undefined;
  const person = session?.user.name || session?.user.email;

  return (
    <aside className={collapsed ? "oc-sidebar oc-sidebar--collapsed" : "oc-sidebar"}>
      <OrgSwitcher
        collapsed={collapsed}
        toggle={<SidebarToggle collapsed={collapsed} onToggle={toggleCollapsed} />}
      />

      <div className="oc-sidebar__section" style={{ borderTop: "none" }}>
        <CommandHint collapsed={collapsed} onOpen={onOpenPalette} />
      </div>

      <nav className="oc-nav">
        {NAV.map((section) => {
          const visible = section.items.filter(shown);

          // A heading over nothing is worse than a flat list: it tells a
          // Support user there is an Audience section and then shows them an
          // empty one. Whole sections do disappear — Platform is four items and
          // Support holds none of their capabilities.
          if (visible.length === 0) return null;

          return (
            <div key={section.heading ?? "top"} className="nav-group">
              {section.heading && <div className="nav-group__heading">{section.heading}</div>}
              {visible.map((item) => {
                const outstanding = attentionFor(items, item.to);
                const severity = severityFor(items, item.to);

                return (
                  // NavLink marks the active route with aria-current="page",
                  // which is exactly what the design's active style keys off —
                  // no extra state.
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className="oc-nav__item"
                    title={
                      collapsed
                        ? outstanding > 0
                          ? `${item.label}: ${outstanding} needing attention`
                          : item.label
                        : undefined
                    }
                  >
                    <Icon name={item.icon} className="oc-nav__icon" />
                    <span className={label}>{item.label}</span>
                    {outstanding > 0 && (
                      // Only when non-zero. A badge that is always present is a
                      // decoration, and the nav is a shortlist rather than a
                      // dashboard.
                      <span
                        className={`nav-badge nav-badge--${severity}`}
                        aria-label={`${outstanding} needing attention`}
                      >
                        {outstanding}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="user">
        <div className="oc-avatar" title={collapsed ? person : undefined}>
          {initials(person)}
        </div>
        <div className={label} style={{ minWidth: 0, flex: 1 }}>
          <div className="oc-body-sm oc-truncate" style={{ fontWeight: 500 }}>
            {person}
          </div>
          <div className="oc-mono-sm oc-text-muted oc-truncate">
            {role ? roleLabel(role) : active ? "" : "no organization"}
          </div>
        </div>
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
          aria-label="Sign out"
          title="Sign out"
          onClick={() => void logout()}
        >
          <Icon name="external" className="oc-nav__icon" />
        </button>
      </div>
    </aside>
  );
}

/**
 * Whether the sidebar shows only its icons, remembered in this browser for
 * each person who signs in to it.
 *
 * Per person for the reason the organization is (session.tsx): on a shared
 * machine, one person's choice is not the next person's. The shell only
 * renders once the session has loaded, and signing in again mounts it afresh,
 * so the key is read once. Storage can be missing or throw, in a private window
 * or with site data blocked; the sidebar then starts expanded, and a choice
 * lasts until the page reloads.
 *
 * [ toggles it from anywhere, as Linear's does, except while somebody types.
 */
function useCollapsed(userId: string | undefined): [boolean, () => void] {
  const key = userId ? `opencomms.sidebar.${userId}` : null;
  const [collapsed, setCollapsed] = useState(() => {
    if (!key) return false;
    try {
      return window.localStorage.getItem(key) === "collapsed";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!key) return;
    try {
      window.localStorage.setItem(key, collapsed ? "collapsed" : "expanded");
    } catch {
      /* kept for this page only, as above */
    }
  }, [key, collapsed]);

  // Stable, so the document listener for [ is bound once.
  const toggle = useCallback(() => setCollapsed((was) => !was), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "[" || event.repeat || event.defaultPrevented) return;
      // Not with a modifier: on a keyboard where [ needs AltGr, Windows reports
      // that as Ctrl+Alt, and the key is then somebody typing a bracket.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Never while somebody types. CodeMirror's editor is contenteditable, so
      // a bracket in a template stays a bracket.
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])")) return;
      toggle();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return [collapsed, toggle];
}

/**
 * Collapses the sidebar to its icons and back, from the top-left corner.
 *
 * The name says what a press will do, so there is no aria-expanded beside it:
 * "Collapse sidebar, expanded" says the same thing twice. The tooltip repeats
 * the name for the pointer and adds the key, which aria-keyshortcuts gives a
 * screen reader instead.
 */
function SidebarToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const name = collapsed ? "Expand sidebar" : "Collapse sidebar";

  return (
    <span className="oc-tooltip sidebar-toggle">
      <button
        type="button"
        className="sidebar-toggle__button"
        aria-label={name}
        aria-keyshortcuts="["
        onClick={onToggle}
      >
        <Icon name="sidebar" size={16} />
      </button>
      <span className="oc-tooltip__bubble" aria-hidden="true">
        {name}
        <kbd className="oc-kbd">[</kbd>
      </span>
    </span>
  );
}

function OrgSwitcher({ collapsed, toggle }: { collapsed: boolean; toggle: ReactNode }) {
  const { session, orgId, switchOrg } = useSession();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useDismiss(open, container, () => setOpen(false));

  // A menu left open across a collapse would jump from under the name to
  // beside the rail, away from whatever the pointer was doing.
  useEffect(() => setOpen(false), [collapsed]);

  const organizations = session?.organizations ?? [];
  const active = organizations.find((o) => o.id === orgId);

  const switchable = organizations.length > 1;
  const name = active?.name ?? "No organization";
  const initial = active ? orgInitial(active.name) : "?";

  return (
    <div ref={container} style={{ position: "relative" }}>
      <div className="org">
        {toggle}
        {!collapsed && (
          <>
            <span className="org-initial" aria-hidden="true">
              {initial}
            </span>
            <button
              type="button"
              className="org__switch"
              aria-haspopup={switchable ? "listbox" : undefined}
              aria-expanded={switchable ? open : undefined}
              aria-label={switchable ? `Organization: ${name}. Switch organization` : `Organization: ${name}`}
              disabled={!switchable}
              onClick={() => setOpen((v) => !v)}
            >
              <span className="oc-headline-md oc-truncate" style={{ color: "var(--oc-text-primary)" }}>
                {name}
              </span>
              {switchable && <Icon name="selector" size={14} />}
            </button>
          </>
        )}
      </div>

      {/* Collapsed, the name leaves and the initial stays, under the toggle:
          the rail has room for one 32px control per row. It opens the menu
          when there is anything to switch to, since the name it opens from is
          no longer on screen. */}
      {collapsed && (
        <div className="org-initial-row">
          {switchable ? (
            <button
              type="button"
              className="org-initial"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-label={`Organization: ${name}. Switch organization`}
              title={`${name}: switch organization`}
              onClick={() => setOpen((v) => !v)}
            >
              {initial}
            </button>
          ) : (
            <span className="org-initial" title={name}>
              <span aria-hidden="true">{initial}</span>
              <span className="oc-visually-hidden">Organization: {name}</span>
            </span>
          )}
        </div>
      )}

      {open && (
        <div className="oc-menu org__menu" role="listbox" aria-label="Switch organization">
          {organizations.map((org) => (
            <button
              key={org.id}
              type="button"
              role="option"
              aria-selected={org.id === orgId}
              className="oc-menu__option org__option"
              onClick={() => {
                switchOrg(org.id);
                setOpen(false);
              }}
            >
              <span className="oc-truncate">{org.name}</span>
              {/* The role is what this column normally says; a pending
                  deletion replaces it rather than sitting beside it, because
                  "owner" is not the fact that matters about this row. */}
              {org.purge_after ? (
                <span className="oc-mono-sm" style={{ color: "var(--oc-danger)" }}>
                  deleting
                </span>
              ) : (
                <span className="oc-mono-sm oc-text-muted">{org.role}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The first letter of an organization's name, for the square beside it.
 *
 * The first letter or digit rather than the first character, so "(Test) Acme"
 * is T rather than a bracket.
 */
function orgInitial(name: string): string {
  const letter = name.match(/[\p{L}\p{N}]/u)?.[0];
  return letter ? letter.toUpperCase() : "?";
}

/**
 * The ⌘K affordance from the design, now opening the palette it advertises.
 *
 * It used to focus `[data-screen-search]`, which exists on three of the
 * thirteen screens — so on the other ten the shortcut was drawn, discoverable,
 * and inert. The shortcut itself is bound in Shell: it has to work from a
 * screen whose focus is inside a drawer or an editor, which is a document-level
 * concern rather than the sidebar's.
 */
function CommandHint({ collapsed, onOpen }: { collapsed: boolean; onOpen: () => void }) {
  const shortcut = `${modifierKey()}K`;

  return (
    <button
      type="button"
      className="oc-cmdk"
      title={collapsed ? `Search (${shortcut})` : undefined}
      onClick={onOpen}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name="search" size={14} />
        <span className={collapsed ? "oc-visually-hidden" : undefined}>Search</span>
      </span>
      <span className={collapsed ? "oc-visually-hidden" : "oc-kbd"}>{shortcut}</span>
    </button>
  );
}

/** Shows the shortcut the way this platform writes it. */
function modifierKey(): string {
  const apple = /Mac|iPhone|iPad/i.test(navigator.userAgent);
  return apple ? "⌘" : "Ctrl+";
}

function initials(name: string | undefined): string {
  if (!name) return "?";

  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
